import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { servicesData } from '../data/mock';

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative min-h-screen bg-black py-24 overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-pink-500/5 to-transparent" />

      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <span className="text-pink-400 text-xs tracking-[0.3em] block mb-4">SERVICES</span>
        <h2 className={`text-white text-3xl md:text-4xl lg:text-5xl font-extralight tracking-wide mb-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {servicesData.title}
        </h2>
        <p className="text-white/50 text-lg max-w-2xl">{servicesData.subtitle}</p>
      </div>

      {/* Services grid */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-8">
          {servicesData.packages.map((pkg, index) => (
            <div
              key={pkg.id}
              className={`group relative overflow-hidden bg-white/[0.02] border border-white/10 hover:border-pink-400/50 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                <div className="relative w-full md:w-1/3 h-48 md:h-auto overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60" />
                </div>

                {/* Content */}
                <div className="flex-1 p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-white text-xl tracking-[0.15em] group-hover:text-pink-400 transition-colors duration-300">
                      {pkg.title}
                    </h3>
                    {pkg.rate && (
                      <span className="text-pink-400 text-lg font-light tracking-wide">
                        {pkg.rate === "Inquire" ? pkg.rate : `$${pkg.rate}`}
                      </span>
                    )}
                  </div>
                  <p className="text-white/50 mb-6 leading-relaxed">
                    {pkg.description}
                  </p>
                  <ul className="space-y-2">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-white/40 text-sm">
                        <Check className="w-4 h-4 text-pink-400 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Hover accent */}
              <div className="absolute bottom-0 left-0 w-0 h-1 bg-pink-400 group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </section>
  );
};

export default ServicesSection;
